import type { components } from "./schema";

/**
 * ADAPTATEUR D'ERREUR — le SEUL fichier à modifier quand le back change d'enveloppe.
 * ============================================================================
 *
 * Pourquoi ce fichier existe (ne pas le supprimer par « simplification ») :
 *
 * **L'enveloppe d'erreur change d'un projet à l'autre.** Constaté :
 *   - back `starter`  : `{ timestamp, status, error, code, message, fieldErrors }`   (plat)
 *   - projet généré   : `{ error: { code, message, fieldErrors? } }`                 (imbriqué)
 *
 * Tout le reste du repo (core, features, écrans) ne connaît QUE `ApiFailure`. Changer de
 * back = réécrire `parseApiFailure` ci-dessous, et **rien d'autre** — une variante prête à
 * l'emploi pour l'enveloppe imbriquée est fournie en fin de fichier.
 *
 * ── Ce qui a changé (2026-08-31) ────────────────────────────────────────────
 * Le back publie désormais `ApiError` dans le contrat, avec `code` déclaré en **enum**, et
 * attache une réponse `default` à toutes les opérations. Le type des codes vient donc du
 * codegen (voir `BackendErrorCode`) au lieu d'être recopié ici, et `parseApiFailure` lit le
 * code **réel** plutôt que de le déduire du statut HTTP.
 *
 * La déduction par statut reste en repli : elle couvre les réponses qui ne viennent pas de
 * l'application (proxy, passerelle) et les backs plus anciens.
 */

/**
 * Codes émis par le back, **tels que déclarés dans le contrat**.
 *
 * Le type vient du codegen : le back publie `code` en `enum` dans le schéma `ApiError`, et
 * `openapi-typescript` en fait une union de littéraux. Conséquence directe : un
 * `switch (failure.code)` est **vérifié à la compilation** (cas oublié signalé, faute de
 * frappe refusée), et un code métier ajouté côté back arrive typé au prochain `pnpm codegen`
 * — sans qu'aucune liste ne soit recopiée ici.
 *
 * Convention actée avec le back : **on ajoute des codes, on n'en renomme jamais.**
 */
export type BackendErrorCode = NonNullable<components["schemas"]["ApiError"]["code"]>;

/** Codes produits par le front, absents du contrat car le serveur n'est pas en cause. */
export const NETWORK_ERROR = "NETWORK_ERROR";
export const UNKNOWN_ERROR = "UNKNOWN_ERROR";

export type ApiFailureCode = BackendErrorCode | typeof NETWORK_ERROR | typeof UNKNOWN_ERROR;

/** Forme normalisée d'une erreur d'API, unique surface exposée au reste de l'app. */
export interface ApiFailure {
  /** Code HTTP. `0` = la requête n'a jamais abouti (réseau coupé, CORS, DNS). */
  readonly status: number;
  /**
   * Code stable, testable en `switch`, **jamais affiché à l'utilisateur**.
   *
   * Vient du champ `code` du back quand il est présent ; à défaut il est dérivé du statut
   * HTTP (compatibilité avec un back plus ancien, ou réponse d'un proxy).
   */
  readonly code: ApiFailureCode | (string & {});
  /** Message humain venant du back. À afficher, JAMAIS à parser. */
  readonly message: string;
  /** Erreurs de validation champ → message. Vide si non applicable. */
  readonly fieldErrors: Readonly<Record<string, string>>;
  /** Corps brut, pour les cas non prévus et le debug. */
  readonly raw: unknown;
}

/** Repli quand la réponse ne porte pas de `code` (proxy, passerelle, back plus ancien). */
function codeFromStatus(status: number): string {
  switch (status) {
    case 400:
      return "VALIDATION_ERROR";
    case 401:
      return "UNAUTHORIZED";
    case 403:
      return "FORBIDDEN";
    case 404:
      return "NOT_FOUND";
    case 409:
      return "CONFLICT";
    case 429:
      return "RATE_LIMITED";
    default:
      return status >= 500 ? "INTERNAL_ERROR" : UNKNOWN_ERROR;
  }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function readFieldErrors(v: unknown): Record<string, string> {
  if (!isObject(v)) return {};
  const out: Record<string, string> = {};
  for (const [k, val] of Object.entries(v)) {
    if (typeof val === "string") out[k] = val;
  }
  return out;
}

/**
 * Traduit une réponse d'erreur du back en `ApiFailure`.
 *
 * Format attendu (back `starter`) :
 *   `{ timestamp, status, error, code, message, fieldErrors }`
 *
 * Tolérant par construction : un corps vide, du HTML (page d'erreur d'un proxy) ou une forme
 * inattendue produisent quand même un `ApiFailure` exploitable — une erreur ne doit jamais
 * en provoquer une seconde.
 */
export function parseApiFailure(status: number, body: unknown): ApiFailure {
  const fallbackMessage = status === 0 ? "Le serveur est injoignable." : `Erreur ${status}.`;

  if (!isObject(body)) {
    return {
      status,
      code: status === 0 ? NETWORK_ERROR : codeFromStatus(status),
      message: typeof body === "string" && body.trim() !== "" ? body : fallbackMessage,
      fieldErrors: {},
      raw: body,
    };
  }

  const message =
    typeof body["message"] === "string" && body["message"] !== ""
      ? body["message"]
      : fallbackMessage;

  return {
    status,
    // Le `code` du back fait autorité. Le repli sur le statut HTTP couvre les réponses qui
    // ne viennent pas de l'application (proxy, passerelle) — elles n'ont pas de `code`.
    code: typeof body["code"] === "string" ? body["code"] : codeFromStatus(status),
    message,
    fieldErrors: readFieldErrors(body["fieldErrors"]),
    raw: body,
  };
}

/** Échec réseau : la requête n'a pas atteint le serveur (coupure, CORS, DNS). */
export function networkFailure(cause: unknown): ApiFailure {
  return {
    status: 0,
    code: NETWORK_ERROR,
    message: "Le serveur est injoignable. Vérifie ta connexion.",
    fieldErrors: {},
    raw: cause,
  };
}

export function isApiFailure(v: unknown): v is ApiFailure {
  return isObject(v) && typeof v["status"] === "number" && typeof v["code"] === "string";
}

/* ----------------------------------------------------------------------------
 * VARIANTE — enveloppe imbriquée `{ error: { code, message, fieldErrors? } }`
 *
 * C'est le format des projets générés par JMO (cf. specs/backend/tech-spec.md §3.2).
 * Il est MEILLEUR : `code` est un vrai code métier (PARTNER_NOT_FOUND, SELF_TRADE…),
 * donc utilisable en `switch` sans deviner.
 *
 * Pour basculer : remplace le corps de `parseApiFailure` par celui-ci. Rien d'autre
 * dans le repo n'a besoin de changer.
 *
 *   export function parseApiFailure(status: number, body: unknown): ApiFailure {
 *     const err = isObject(body) && isObject(body["error"]) ? body["error"] : undefined;
 *     if (!err) return { status, code: codeFromStatus(status), message: `Erreur ${status}.`,
 *                        fieldErrors: {}, raw: body };
 *     return {
 *       status,
 *       code: typeof err["code"] === "string" ? err["code"] : codeFromStatus(status),
 *       message: typeof err["message"] === "string" ? err["message"] : `Erreur ${status}.`,
 *       fieldErrors: readFieldErrors(err["fieldErrors"]),
 *       raw: body,
 *     };
 *   }
 * -------------------------------------------------------------------------- */
